/** Shared text-overflow measurement for titles and labels; tooltips are enabled only while their text is clipped. */
import { DestroyRef, Directive, ElementRef, inject, signal } from '@angular/core';

/**
 * `scrollWidth` rounds a fractional content width up while `clientWidth` rounds it, so a value that
 * fits exactly can still report one pixel of overflow and claim a tooltip it does not need. Same
 * guard `wb.repeat.table.component.ts` applies to its own overflow test.
 */
const SUB_PIXEL_TOLERANCE = 1;

/**
 * Reports whether the host's text is cut off by `text-overflow`, as a signal.
 *
 * Exists because the obvious form — `[tooltipDisabled]="!isTruncated(el)"` calling a DOM read from
 * the template — cannot be trusted in an `OnPush` view, and every consumer of it here is one:
 *
 *  - the read runs mid-update-pass, where the element's own text interpolation may not be written
 *    yet, so the first answer is measured against an empty box;
 *  - a hover does not dirty an `OnPush` view, so PrimeNG keeps whatever `disabled` it latched at the
 *    last check — the answer never gets corrected afterwards.
 *
 * A `ResizeObserver` measures once layout exists instead, and writing the result to a signal is what
 * dirties the view when it flips. Both existing hand-rolled copies in this repo
 * (`wb.value.component.ts`, `form.response.items.cell.renderer.ts`) read from the template; promote
 * this directive over them if either turns out to misreport for the same reason.
 *
 * Usage — the host needs the ellipsis CSS itself; this only measures:
 *
 *   <span #t="truncatedText" truncatedText [pTooltip]="value" [tooltipDisabled]="!t.$truncated()">
 */
@Directive({
    selector: '[truncatedText]',
    standalone: true,
    exportAs: 'truncatedText'
})
export class TruncatedTextDirective {

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);

    private readonly _$truncated = signal(false);

    /** True while the host's text does not fit its box. */
    public readonly $truncated = this._$truncated.asReadonly();

    public constructor() {

        const element = this.host.nativeElement;

        // Observes the element, not the window: a chip is also resized by its own value changing and
        // by the header bar reflowing around it, neither of which a resize listener would catch.
        // The observer's first delivery is the initial measure — measuring here instead would only
        // publish a false negative for the frame before layout exists.
        const observer = new ResizeObserver(() => this._$truncated.set(element.scrollWidth > element.clientWidth + SUB_PIXEL_TOLERANCE));

        observer.observe(element);

        this.destroyRef.onDestroy(() => observer.disconnect());
    }
}
